/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx, Themed } from "theme-ui";
import React from "react";
import _ from "lodash";
import FAQPanel from "./faq-panel";
import FAQJsonLd from "./faq-json-ld";
import { useFAQs } from ".";

export type FAQProps = {
  className?: string;
  heading?: string;
  useContexts?: () => React.FC;
};

const FAQSection: React.FC<FAQProps> = ({
  className,
  heading = "Frequently Asked Questions",
  useContexts = () =>
    // eslint-disable-next-line react/display-name
    ({ children }) =>
      <>{children}</>,
}) => {
  const ContextProvider = useContexts();
  const faqs = useFAQs();
  if (!faqs || _.isEmpty(faqs)) {
    return null;
  }
  //  We de-dup based on title, to allow overriding auto-generated FAQs with manual ones,
  //  by reversing, uniquing, reversing, in order to take the keep the most recent version.
  //  Make a copy via slice(0) because the readonly array can't be reversed in-place.
  const dedupedFAQs = _.reverse(_.uniqBy(_.reverse(faqs.slice(0)), faq => _.lowerCase(faq.title)));
  // Some FAQs might have been rendered inline in the article, and don't need to be rendered here.
  //  In either case, though, they should generate schema.
  const faqsToRender = _.filter(dedupedFAQs, (faq) => faq.render === "with-faqs");
  return (
    <>
      <FAQJsonLd faqs={dedupedFAQs} render={ContextProvider} />
      {faqsToRender.length ? (
        <section className={className} sx={{ mx: 5 }}>
          <hr sx={{ mt: 3 }} />
          <Themed.h2 id="faq">{heading}</Themed.h2>
          {_.map(faqsToRender, (faq) => (
            <FAQPanel key={faq.title} faq={faq} />
          ))}
        </section>
      ) : null}
    </>
  );
};

export default FAQSection;
